import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, interval as observableInterval, Subject } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { GridReadyEvent, IRowNode } from 'ag-grid-community';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../Compliance/AgGrid';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { TaxRateCommandCenterService } from './tax.rate.command.center.service';
import { TaxAuthority, TaxRateArea } from '../../Assessor-Collector/Tax-Rates/tax.rates.panel.model';
import { Constants, EntityType } from '../../constants.new';
import {
    MessageBoxButtons,
    MessageBoxResult,
    MessageBoxService
} from '../../UI-Lib/Message-Box/messagebox.service.upgrade';
import { GridSource, TaxRateGridComponent } from '../tax.rate.grid.component';
import {
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectTracker
} from '../../Compliance/AgGrid/MultiSelectTracker';
import {
    TaxAuthorityBulkUpdateComponent,
    TaxAuthorityBulkUpdateModalParams
} from './Tax-Authority-Bulk-Update/tax.authority.bulk.update.component';
import {
    QcStateCount,
    TaxAuthorityBulkUpdateRequest
} from '../tax.rate.model';
import { TaxRateCommandCenterAgGridDataSource, TaxRateDataSourceParams } from './agGridDataSource';
import {
    ICellRendererParamsForTaxRateCommandCenterComments,
    TaxRateCommandCenterCommentsCellRendererComponent
} from './agGridCommentsCellRenderer.component';
import {
    ICellRendererParamsForTaxRateCommandCenterAttachments,
    TaxRateCommandCenterAttachmentsCellRendererComponent
} from './agGridAttachmentsCellRenderer.component';
import {
    AgGridExportOptions,
    AgGridExportStartLRP,
    AgGridToolPanelButton
} from '../../Compliance/AgGrid/ToolPanel/models';
import { WeissmanDateFormatPipe } from '../../UI-Lib/Pipes';
import { OrderedColDef } from '../../Common/AgGrid/ag.grid.model';

@Component({
    selector: 'tax-rate-command-center',
    templateUrl: './tax.rate.command.center.component.html',
    styleUrls: ['./tax.rate.command.center.component.scss'],
    standalone: false,
})
export class TaxRateCommandCenterComponent extends TaxRateGridComponent implements OnInit, OnDestroy {
    constructor(
        public taxRateCommandCenterService: TaxRateCommandCenterService,
        private readonly _weissmanModalService: WeissmanModalService,
        private readonly _modalService: BsModalService,
        private readonly _constants: Constants,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _datePipe: WeissmanDateFormatPipe,
        private readonly _toastr: ToastrService,
        private readonly restrictService: RestrictService,
    ) {
        super(taxRateCommandCenterService, restrictService, _weissmanModalService);
        this.hasDataImportPermission = restrictService.isInRole(Roles.ALLOWDATAIMPORTS);
    }

    loading: boolean = false;
    isMaximized: boolean = false;
    hasDataImportPermission: boolean = false;

    stateAssessors: Compliance.NameValuePair<number>[] = [];
    stateCollectors: Compliance.NameValuePair<number>[] = [];
    assessorCollectors: Compliance.NameValuePair<number>[] = [];
    assessorsCollectorsLoading: boolean = false;

    selectedStateId: number;
    selectedAssessorId: number;
    selectedCollectorId: number;
    onlyPendingQC: boolean;

    gridId: System.Guid;
    gridTracker: AgGridMultiSelectTracker;
    isBulkUpdateVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    pendingQcStateCounts: QcStateCount[] = [];
    qcCountTotal: number;
    qcCountsUpdating: boolean = false;

    LARGE_BULK_UPDATE_THRESHOLD: number = 100;

    selectedNode: IRowNode;

    gridOptions = new AgGridOptionsBuilder({
        rowClassRules: {
            'ag-row-selected': (params) =>
                params.data && this.gridTracker.isRowSelected((params.data as Core.TaxAuthorityDTO).taxAuthorityId),
        },
        onRowClicked: (params) => {
            if (!params.data || this.qcCountsUpdating || params.event.cancelBubble) {
                return;
            }

            this.selectedNode = params.node;

            if (this.selectedGridSourceOption === 'areas') {
                this.launchTaxRateAreaModal(params.data.taxRateAreaId, false);
            } else {
                this.launchTaxAuthorityModal(params.data.taxAuthorityId, false);
            }
        },
        onCellClicked: (params) => {
            if (params.colDef.colId === 'grid-column-multiselect') {
                params.event.stopPropagation();
            }
        },
        onFilterChanged: () => this.gridTracker.onGridFilterChanged(),
        onSortChanged: () => this.gridTracker.onGridSortChanged(),
        onColumnVisible: () => {
            this.gridApi.autoSizeAllColumns();
        },
    })
        .withRowId((row) => `${row.data.taxAuthorityId}`)
        .withColumnPinning()
        .buildDefault(this);

    exportOptions: AgGridExportOptions = {
        start: async (columnsToReturn: Compliance.NameValuePair<string>[]): Promise<AgGridExportStartLRP> => {
            const searchParams = this._gridDataSource.getSearchModelWithoutPagination();

            searchParams.selectedRows = this.gridTracker.getSelectedRowsModel();

            if (!searchParams.selectedRows.selectAllRows && searchParams.selectedRows.selectedRows.length === 0) {
                searchParams.selectedRows.selectAllRows = true;
            }

            searchParams.pagination = {
                skip: 0,
                take: this._gridDataSource.totalRows,
            };

            const exportModel = {
                searchModel: searchParams,
                columnsToReturn: columnsToReturn,
            } as Core.TaxRateExportModel;

            const longRunningProcessId = await this.taxRateCommandCenterService.exportTaxRateData(exportModel);

            return {
                longRunningProcessId,
                longRunningProcessTypeId: Compliance.LongRunningProcessTypeEnum.ExportTaxRates,
            };
        },
        canCancel: true,
    };

    additionalButtons: AgGridToolPanelButton[];

    private readonly _TAX_AUTHORITIES_GRID_ID: System.Guid = 'E73E3CB3-2AD9-43F5-9DA1-645272848A80';
    private readonly _TAX_RATE_AREAS_GRID_ID: System.Guid = '065A0AD6-98E4-41B7-84CE-2857CBA47777';
    private _gridDataSource: TaxRateCommandCenterAgGridDataSource;
    private _canRetry: boolean;
    private _agGridReady: boolean;
    private _selectedNodes: { [id: string]: Core.QueueUpdateStatusEnum } = {};

    private _destroy$: Subject<void> = new Subject();

    get gridLoading(): boolean {
        return this.loading || this.assessorsCollectorsLoading || this._gridDataSource?.isRefreshing;
    }

    get isRetryDisabled(): boolean {
        return this.gridTracker?.getSelectedRowsCount() === 0 || !this._canRetry || this._gridDataSource.isRefreshing;
    }

    get isRetryVisible(): boolean {
        return (
            this.selectedGridSourceOption !== 'areas' &&
            this.restrictService.isInRole(Roles.TAXRATESETUP) &&
            this.restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT)
        );
    }

    async ngOnInit() {
        this.gridId = this._TAX_AUTHORITIES_GRID_ID;

        if (this.isRetryVisible) {
            this.additionalButtons = [
                {
                    icon: 'fa-rotate',
                    getTooltipText: () => 'Retry',
                    disabled: () => this.isRetryDisabled,
                    onClickCallback: () => this.retry(),
                },
            ];
        }

        observableInterval(60000)
            .pipe(
                takeUntil(this._destroy$),
                startWith(0),
                switchMap(() => {
                    this.qcCountsUpdating = true;
                    return this.taxRateCommandCenterService.getPendingQcCounts();
                }),
            )
            .subscribe((data) => {
                this.qcCountsUpdating = false;
                this.pendingQcStateCounts = data;

                this._getQcCountTotal();
            });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onAgGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;

        if (!this.gridTracker) {
            this.gridTracker = new AgGridMultiSelectTracker(
                this.gridOptions,
                this.gridApi,
                this._getGridRowIds.bind(this),
            );

            this.gridTracker.selectedRows$.pipe(takeUntil(this._destroy$)).subscribe(async (selectedRowsModel) => {
                const isBulkUpdateVisible =
                    this.restrictService.isInRole(Roles.TAXRATESETUP) &&
                    this.selectedGridSourceOption === 'authorities' &&
                    this.gridTracker.getSelectedRowsCount() > 0;
                this.isBulkUpdateVisible$.next(isBulkUpdateVisible);

                if (this.selectedGridSourceOption === 'authorities') {
                    const selectedRows = await this.gridTracker.getSelectedRowIds();
                    selectedRows.forEach((rowId) => {
                        const rowNode = this.gridApi.getRowNode(rowId.toString());
                        const data = rowNode?.data as Core.TaxAuthorityDTO;
                        if (data) {
                            this._selectedNodes[rowId] = data.queueProcessingStatus;
                        }
                    });

                    this._canRetry =
                        selectedRows.length &&
                        selectedRows.every((rowId) => this._selectedNodes[rowId] === Core.QueueUpdateStatusEnum.Error);
                }
            });
        }

        if (!this._agGridReady) {
            this.gridApi.setGridOption('columnDefs', this.getGridColumns());
        }
        this._agGridReady = true;

        this._setDataSource();
    }

    toggleMaximize(isMaximized: boolean): void {
        this.isMaximized = isMaximized;
    }

    gridSourceSelected(gridSource: GridSource): void {
        if (this.selectedGridSourceOption === gridSource) {
            return;
        }
        this._changeGridSource(gridSource);
        this._refreshDataSource();
    }

    filterGridByStateAndQc(stateId: number) {
        this.selectedStateId = stateId;
        this.onlyPendingQC = true;

        if (this.selectedGridSourceOption === 'areas') {
            this._changeGridSource('authorities');
        }

        this.stateSelected();
    }

    async refreshQcCounts() {
        this.qcCountsUpdating = true;
        this.gridApi.setGridOption('loading', true);

        this.pendingQcStateCounts = await this.taxRateCommandCenterService.getPendingQcCounts();
        this._getQcCountTotal();

        this.gridApi.setGridOption('loading', false);
        this.qcCountsUpdating = false;
    }

    async stateSelected(): Promise<void> {
        if (!this.selectedStateId) {
            this.stateAssessors = this.stateCollectors = [];
        } else {
            this.assessorsCollectorsLoading = true;

            Promise.all([
                this.taxRateCommandCenterService.getAssessors(this.selectedStateId),
                this.taxRateCommandCenterService.getCollectors(this.selectedStateId),
            ]).then(([assessors, collectors]) => {
                this.stateAssessors = assessors.sort(this._abbrSort).map((a) => ({
                    name: `${a.abbr} ${a.municipalityName ? a.municipalityName : ''}`,
                    value: a.assessorID,
                }));

                this.stateCollectors = collectors
                    .sort(this._abbrSort)
                    .map((a) => ({ name: `${a.abbr}`, value: a.collectorID }));

                this.assessorsCollectorsLoading = false;
            });
        }

        this.selectedAssessorId = null;
        this.selectedCollectorId = null;

        this._refreshDataSource();
    }

    async assessorSelected(): Promise<void> {
        this.selectedCollectorId = null;
        this._refreshDataSource();

        if (!this.selectedAssessorId) {
            this.assessorCollectors = [];

            return;
        }
        this.assessorsCollectorsLoading = true;

        const collectorAssociations = await this.taxRateCommandCenterService.getCollectors(
            this.selectedStateId,
            this.selectedAssessorId,
        );

        this.assessorCollectors = collectorAssociations
            .sort(this._abbrSort)
            .map((a) => ({ name: `${a.abbr}`, value: a.collectorID }));

        this.assessorsCollectorsLoading = false;
    }

    collectorSelected() {
        this._refreshDataSource();
    }

    showTaxYearChangedCC() {
        this.showTaxYearChanged();
        this._refreshDataSource();
    }

    onlyPendingQCChanged() {
        this._refreshDataSource();
    }

    async bulkUpdate(): Promise<void> {
        this.loading = true;
        let selections: Core.TaxAuthorityToUpdate[] = [];

        try {
            const selectionOptions: Core.TaxRateSearchModel = this._gridDataSource.getSearchModelForBulkUpdate(0, 0);
            selections = await this.taxRateCommandCenterService.getTaxAuthoritySelectionsForBulkUpdate(selectionOptions);
        } catch (error) {
            this._toastr.error(
                this._getErrorMessage(error, 'Tax authority bulk update failed. Please try again.'),
                'Bulk update failed',
            );
            return;
        } finally {
            this.loading = false;
        }

        if (selections.length === 0) {
            return;
        }

        if (selections.length > this.LARGE_BULK_UPDATE_THRESHOLD) {
            const answer: number = await this._messageBoxService.open({
                message: `Are you sure you want to Bulk Update ${selections.length} Tax Authorities?`,
                buttons: MessageBoxButtons.OKCancel,
            });

            if (answer !== MessageBoxResult.OK) {
                return;
            }
        }

        const params: TaxAuthorityBulkUpdateModalParams = {
            selectedCount: selections.length,
            selections,
        };

        const showModalFn = (): BsModalRef => {
            return this._modalService.show(TaxAuthorityBulkUpdateComponent, {
                class: 'modal-lg',
                initialState: {
                    params,
                },
                ignoreBackdropClick: true,
                keyboard: false,
            });
        };

        const modalRef = this._weissmanModalService.show(showModalFn.bind(this));
        const bulkUpdateModal = modalRef.bsModalRef.content as TaxAuthorityBulkUpdateComponent;
        bulkUpdateModal.onClose = async (request?: TaxAuthorityBulkUpdateRequest) => {
            if (!request) {
                return;
            }

            bulkUpdateModal.loading = true;

            try {
                await this.processBulkUpdateRequest(request, selections);
                modalRef.bsModalRef.hide();
            } catch (error) {
                bulkUpdateModal.loading = false;
                this._toastr.error(
                    this._getErrorMessage(error, 'Tax authority bulk update failed. Please try again.'),
                    'Save failed',
                );
            }
        };
    }

    refresh() {
        this._refreshDataSource();
        this._refreshAssessorCollectors();
    }

    async retry(): Promise<void> {
        const taxAuthorityIds = await this.gridTracker.getSelectedRowIds();
        await this.taxRateCommandCenterService.retryProcessingTaxAuthorities(taxAuthorityIds);

        const retriedNodes: IRowNode[] = [];
        this.gridApi.forEachNode((x) => {
            const gridRow = x.data as Core.TaxAuthorityDTO;
            if (gridRow.taxAuthorityId && taxAuthorityIds.indexOf(gridRow.taxAuthorityId) !== -1) {
                gridRow.queueProcessingStatus = Core.QueueUpdateStatusEnum.Queued;
                gridRow.queueProcessingStatusName = 'Requested, Pending';
                retriedNodes.push(x);
            }
        });

        this.gridApi.redrawRows({ rowNodes: retriedNodes });

        this.gridTracker.clear();
        this._selectedNodes = {};
        this._canRetry = false;
    }

    protected async processBulkUpdateRequest(
        request: TaxAuthorityBulkUpdateRequest,
        taxAuthorities: Core.TaxAuthorityToUpdate[],
    ): Promise<void> {
        if (request) {
            const model: Core.TaxAuthoritiesBulkUpdateRequest = {
                taxAuthorityIds: taxAuthorities.map((x) => x.taxAuthorityId),
                categoryAction: request.categoryAction,
                taxAuthorityCategoryId: request.taxAuthorityCategoryId,
                taxableAssessmentTypeId: request.taxableAssessmentTypeId,
                taxableAssessmentTypeAction: request.taxableAssessmentTypeAction,
                taxAuthorityExemptionTypeId: request.taxAuthorityExemptionTypeId,
                taxAuthorityExemptionTypeAction: request.taxAuthorityExemptionTypeAction,
                allowExemptionsOnAlternateAV: request.allowExemptionsOnAlternateAV,
                allowedExemptionAssessmentComponentSelections: request.allowedExemptionAssessmentComponentSelections,
                certificationDateAction: request.certificationDateAction,
                certificationDay: request.certificationDay,
                certificationMonth: request.certificationMonth,
                certificationType: request.certificationType,
                obtainInAdvanceAction: request.obtainInAdvanceAction,
                obtainInAdvance: request.obtainInAdvance,
                defaultAnnualIncreaseAction: request.defaultAnnualIncreaseAction,
                defaultAnnualIncrease: request.defaultAnnualIncrease,
                yoyAction: request.yoyAction,
                yoyPercent: request.yoyPercent,
                taxYear: request.taxYear,
            };

            await this.taxRateCommandCenterService.bulkUpdate(model);
            this.refresh();
        }
    }

    protected loadPanel(entity: TaxRateArea | TaxAuthority): void {
        if (entity) {
            this.refresh();
        }
    }

    private _getErrorMessage(error: unknown, fallbackMessage: string): string {
        const err = error as { error?: { message?: string } } | null;
        return err?.error?.message || fallbackMessage;
    }

    protected getSharedColumns(): OrderedColDef[] {
        const cols = [];
        if (this.selectedGridSourceOption === 'authorities') {
            cols.push({
                colId: 'grid-column-multiselect',
                headerName: '',
                field: 'taxAuthorityId',
                width: AgGridColumns.selectionColumnWidth,
                minWidth: AgGridColumns.selectionColumnWidth,
                maxWidth: AgGridColumns.selectionColumnWidth,
                lockVisible: true,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                editable: false,
                resizable: false,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
                sequence: 0,
            });
        }
        return [
            ...cols,
            {
                colId: 'code',
                headerName: 'Code',
                field: 'code',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 2,
            },
            {
                colId: 'address',
                headerName: 'Address',
                field: 'address',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => {
                    const entity = params.data as Core.TaxAuthorityDTO | TaxRateArea;
                    if (!entity || !entity.address) {
                        return '';
                    }

                    const address = entity.address;
                    let addressString: string = '';

                    addressString += address.address1 ? `${address.address1},` : '';
                    addressString += address.address2 ? ` ${address.address2},` : '';
                    addressString += address.city ? ` ${address.city},` : '';
                    addressString += address.abbr ? ` ${address.abbr}` : '';
                    addressString += address.zip ? ` ${address.zip}` : '';

                    return addressString;
                },
                sequence: 3,
            },
            {
                colId: 'assessorName',
                headerName: 'Assessor',
                field: 'assessorName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 8.1,
            },
            {
                colId: 'collectorName',
                headerName: 'Collector',
                field: 'collectorName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 8.2,
            },
            {
                colId: 'hasAttachments',
                headerName: '',
                field: 'hasAttachments',
                filter: false,
                width: AgGridColumns.textColumnSmallWidth,
                cellRenderer: TaxRateCommandCenterAttachmentsCellRendererComponent,
                cellRendererParams: {
                    gridSofurce: this.selectedGridSourceOption,
                    canEdit: this.hasTaxRatePermission,
                } as ICellRendererParamsForTaxRateCommandCenterAttachments,
                sequence: 9,
            },
            {
                colId: 'hasComments',
                headerName: '',
                field: 'hasComments',
                filter: false,
                width: AgGridColumns.textColumnSmallWidth,
                cellRenderer: TaxRateCommandCenterCommentsCellRendererComponent,
                cellRendererParams: {
                    gridSofurce: this.selectedGridSourceOption,
                    canEdit: this.hasTaxRatePermission,
                } as ICellRendererParamsForTaxRateCommandCenterComments,
                sequence: 10,
            },
        ];
    }

    protected getAuthorityColumns(): OrderedColDef[] {
        const columns = [
            {
                colId: 'name',
                headerName: 'Authority Name',
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 1,
            },
            {
                colId: 'ptxId',
                headerName: 'PTX ID',
                field: 'ptxId',
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 2.1,
            },
            {
                colId: 'categoryName',
                headerName: 'Category',
                field: 'categoryName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || !taxAuthority.taxAuthorityCategoryId) {
                        return '';
                    }

                    return this._constants.TaxAuthorityCategories[taxAuthority.taxAuthorityCategoryId]?.displayName;
                },
                sequence: 4,
            },
            {
                colId: 'taxableAssessmentType',
                headerName: 'Taxable Assessment',
                field: 'taxableAssessmentType',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || !taxAuthority.taxableAssessmentTypeId) {
                        return '';
                    }

                    const displayText: any =
                        this._constants.TaxableAssessmentTypesForTrueRate[taxAuthority.taxableAssessmentTypeId]
                            ?.displayName;

                    return displayText || '';
                },
                sequence: 5,
            },
            {
                colId: 'taxAuthorityExemptionType',
                headerName: 'Exemptions Honored',
                field: 'taxAuthorityExemptionType',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sortable: false,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || !taxAuthority.taxAuthorityExemptionTypeId) {
                        return '';
                    }

                    let displayText: any =
                        this._constants.TaxAuthorityExemptionTypes[taxAuthority.taxAuthorityExemptionTypeId]
                            ?.displayName;

                    if (
                        taxAuthority.taxAuthorityExemptionTypeId ===
                        Core.TaxAuthorityExemptionTypeEnum.CertainExemptions
                    ) {
                        displayText = taxAuthority.exemptionsHonored.join(', ');
                    }

                    return displayText;
                },
                sequence: 5.1,
            },
            {
                colId: 'qcRequestUserFullName',
                headerName: 'QC By',
                field: 'qcRequestUserFullName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 5.2,
            },
            {
                colId: 'qcRequestTimeUtc',
                headerName: 'QC Request Date',
                field: 'qcRequestTimeUtc',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => (params.value ? this._datePipe.transform(params.value, true) : ''),
                sequence: 5.3,
            },
            {
                colId: 'certificationDate',
                headerName: 'Certification Date',
                field: 'certificationDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;
                    if (!taxAuthority || !taxAuthority.certificationDate) {
                        return '';
                    }
                    return this._datePipe.transform(taxAuthority.certificationDate, true);
                },
                sequence: 6,
            },
            {
                colId: 'obtainInAdvance',
                headerName: 'Obtain in Advance',
                // headerClass: 'ws-text-align-center',
                field: 'obtainInAdvance',
                filter: true,
                filterParams: AgGridFilterParams.booleanFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.booleanFloatingFilterParams,
                width: AgGridColumns.textColumnSmallWidth,
                cellRenderer: (params) => {
                    const entity = params.data as Core.TaxAuthorityDTO;

                    if (!entity) {
                        return '';
                    }

                    if (entity.obtainInAdvance) {
                        return '<i class="fa fa-check"></i>';
                    } else {
                        return '';
                    }
                },
                sequence: 7,
                // cellClass: 'ws-text-align-center'
            },
            {
                colId: 'defaultAnnualRateIncrease',
                headerName: 'Default Annual Rate Increase %',
                headerClass: 'text-align-right',
                field: 'defaultAnnualRateIncrease',
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                width: AgGridColumns.numericColumnWidth,
                valueFormatter: (params) => {
                    if (!params.data || !params.data.defaultAnnualRateIncrease) {
                        return;
                    }

                    return new Decimal(params.data.defaultAnnualRateIncrease).times(100).toFixed(2);
                },
                cellClass: 'text-align-right',
                sequence: 8,
            },
            {
                colId: 'queueProcessingStatusName',
                headerName: 'Status Update',
                field: 'queueProcessingStatusName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                hide: true,
                sequence: 8.3,
            },
            {
                colId: 'taxAuthorityId',
                headerName: 'System ID',
                field: 'taxAuthorityId',
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                width: AgGridColumns.numericColumnWidth,
                cellClass: 'text-align-right',
                hide: true,
                sequence: 1.1,
            },
        ];

        return columns;
    }

    protected getAreaColumns(): OrderedColDef[] {
        return [
            {
                colId: 'name',
                headerName: 'Rate Area Name',
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sequence: 1,
            },
        ];
    }

    private _changeGridSource(gridSource: GridSource): void {
        this.selectedGridSourceOption = gridSource;
        this.gridId = gridSource === 'authorities' ? this._TAX_AUTHORITIES_GRID_ID : this._TAX_RATE_AREAS_GRID_ID;

        this.gridTracker.clear();

        this.gridApi.setGridOption('columnDefs', []);
        this.gridApi.setGridOption('columnDefs', this.getGridColumns());
    }

    private _getQcCountTotal(): void {
        this.qcCountTotal = this.pendingQcStateCounts.reduce((total, state) => {
            return total + state.item3;
        }, 0);
    }

    private async _refreshAssessorCollectors() {
        if (!this.selectedStateId) {
            return;
        }

        this.assessorsCollectorsLoading = true;
        const promises: Promise<any>[] = [this.taxRateCommandCenterService.getAssessors(this.selectedStateId)];

        if (this.selectedAssessorId) {
            promises.push(
                this.taxRateCommandCenterService.getCollectors(this.selectedStateId, this.selectedAssessorId),
            );
        } else {
            promises.push(this.taxRateCommandCenterService.getCollectors(this.selectedStateId));
        }

        const [assessors, collectors] = await Promise.all(promises);
        this.stateAssessors = assessors
            .sort(this._abbrSort)
            .map((a) => ({ name: `${a.abbr} ${a.municipalityName ? a.municipalityName : ''}`, value: a.assessorID }));

        if (this.selectedAssessorId) {
            if (!this.stateAssessors.some((x) => x.value === this.selectedAssessorId)) {
                this.selectedAssessorId = null;
            }

            this.assessorCollectors = collectors.sort(this._abbrSort);

            if (
                this.selectedCollectorId &&
                !this.assessorCollectors.some((x) => x.value === this.selectedCollectorId)
            ) {
                this.selectedCollectorId = null;
            }
        } else {
            this.stateCollectors = collectors
                .sort(this._abbrSort)
                .map((a) => ({ name: `${a.abbr}`, value: a.collectorID }));

            if (this.selectedCollectorId && !this.stateCollectors.some((x) => x.value === this.selectedCollectorId)) {
                this.selectedCollectorId = null;
            }
        }

        this.assessorsCollectorsLoading = false;
    }

    private _refreshDataSource() {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }
        this._gridDataSource.cancelRequest();
        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        const dataSourceParams = (): TaxRateDataSourceParams => {
            return {
                entityType:
                    this.selectedGridSourceOption === 'areas' ? EntityType.TaxRateArea : EntityType.TaxAuthority,
                stateId: this.selectedStateId,
                assessorId: this.selectedAssessorId,
                collectorId: this.selectedCollectorId,
                onlyPendingQC: this.onlyPendingQC,
                taxYearBegin:
                    this.taxRateCommandCenterService.taxYearBegin - this.taxRateCommandCenterService.taxYearsPrior,
                taxYearEnd: this.taxRateCommandCenterService.taxYearBegin,
            };
        };

        this._gridDataSource = new TaxRateCommandCenterAgGridDataSource(
            this.gridApi,
            this.gridTracker,
            this.taxRateCommandCenterService,
            dataSourceParams,
        );

        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }

    private async _getGridRowIds(skip: number, take: number): Promise<Compliance.QueryResultModel<number>> {
        const result = await this._gridDataSource.getRowIdsInternal(skip, take);
        this._canRetry = result.some((x) => x.queueProcessingStatus !== Core.QueueUpdateStatusEnum.Error);
        return Promise.resolve({
            data: result.map((x) => x.entityId),
            totalRows: result.length,
            lastModifiedTimestamp: new Date(),
            totalValidRows: 0,
        });
    }

    private _abbrSort = (a, b) => (a.abbr > b.abbr ? 1 : -1);
}
